import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectKeyDataPendingOrders, selectExistingPaymentLink, setExistingPaymentLink } from '../../redux/features/keyDataSlice';
import { calculateOrdersSummary, generateSummaryText } from '../../utils/orderUtils';
import { Radio, RadioGroup, FormControlLabel, FormControl, FormLabel } from '@mui/material';
import { toast } from 'react-toastify';
import useManageOrder from '../order/useManageOrder';
import { formatAmountRound } from '../../utils/formatAmount';
import useBankTransferOnly from '../../hooks/useBankTransferOnly';
import { COMPANY_BANK_NOTICE, companyBankText } from '../../utils/companyBank';
import AuctionChargeModal from './AuctionChargeModal';

const womenList = ['Eve', 'Meen', 'Phat', 'Kluay','Pla'];

/**
 * @param {object} props
 * @param {object} props.user - logged-in user (nickname for gender suffix)
 * @param {object} [props.pendingOrdersOverride] - when set, use instead of Redux (e.g. backoffice payment-summary queue)
 * @param {string} [props.shopNameProp] - when set, use for summary wording + payment API shop instead of Redux shop
 * @param {function} [props.onWalletCharged] - after "เก็บเงิน" charged a platform customer's wallet
 */
const OrderAmount = ({ user, pendingOrdersOverride = null, shopNameProp = null, onWalletCharged }) => {
  const dispatch = useDispatch();
  const pendingOrdersFromRedux = useSelector(selectKeyDataPendingOrders);
  const pendingOrders = pendingOrdersOverride ?? pendingOrdersFromRedux;
  const existingPaymentLink = useSelector(selectExistingPaymentLink);
  const [ordersSummary, setOrdersSummary] = useState({});
  const [grandTotalState, setGrandTotalState] = useState('');
  const [copyMessage, setCopyMessage] = useState('');
  const [gender, setGender] = useState('ครับ');
  const [paymentMethod, setPaymentMethod] = useState('qrCode');
  const [summaryText, setSummaryText] = useState('');
  const [copiedForSettlement, setCopiedForSettlement] = useState(false);
  const [isChargeOpen, setIsChargeOpen] = useState(false);
  const shopNameFromRedux = useSelector((state) => state.shop.name);
  const effectiveShop = shopNameProp ?? shopNameFromRedux;
  
  const { createFirstOrderPaymentLink, isLoading } = useManageOrder();

  // Check if customer is company customer
  const isCompanyCustomer = pendingOrders?.isCompanyCustomer || false;
  // A platform customer (has a login) pays from their Wallet: admin charges it with
  // "เก็บเงิน" — no payment link, no bank account text.
  const chargesWallet = !!pendingOrders?.userId;
  // While links are off, company customers get plain copy text with the company account
  const bankTransferOnly = useBankTransferOnly();
  const usesPaymentLink = isCompanyCustomer && !bankTransferOnly && !chargesWallet;
  const needsCompanyBank = isCompanyCustomer && bankTransferOnly && !chargesWallet;
  // Get advance amount from pendingOrders
  const advanceAmount = pendingOrders?.advanceAmount || 0;
  // Non-company customer whose credit fully covers the bill (net ≤ 0). After the admin
  // copies the "nothing to pay" message there is no slip to prompt them, so it's easy to
  // forget to settle it — we show a reminder to complete it at "จ่ายค่าส่ง".
  const creditCoversAll = !isCompanyCustomer && advanceAmount > 0 && ((Number(grandTotalState) || 0) - advanceAmount) <= 0;

  // Reset the reminder when the selected customer/orders change
  useEffect(() => { setCopiedForSettlement(false); }, [pendingOrders]);

  useEffect(() => {
    if (pendingOrders && Array.isArray(pendingOrders.orders)) {
      const result = calculateOrdersSummary(pendingOrders.orders);
      setOrdersSummary(result.summary);
      setGrandTotalState(result.grandTotal);
      
      // Generate initial summary text
      let initialText = generateSummaryText(result.summary, result.grandTotal, gender, effectiveShop === 'tama');
      
      // Add advance amount information for non-company customers only
      if (!isCompanyCustomer && advanceAmount > 0) {
        const netDue = Math.max(0, result.grandTotal - advanceAmount);
        initialText += `\nมียอดฝากไว้: ${formatAmountRound(advanceAmount)} บาท\n`;
        if (result.grandTotal - advanceAmount > 0) {
          // Credit partially covers the order — customer still owes the remainder
          initialText += `ยอดรวมที่ต้องชำระทั้งหมด: ${formatAmountRound(result.grandTotal)} - ${formatAmountRound(advanceAmount)} = ${formatAmountRound(netDue)} บาท`;
        } else {
          // Credit fully covers the order (net ≤ 0) — nothing to transfer
          initialText += `ยอดที่ฝากไว้ครอบคลุมยอดทั้งหมด — ไม่มียอดที่ต้องชำระ ✅`;
          const carryForward = advanceAmount - result.grandTotal;
          if (carryForward > 0) {
            initialText += `\nยอดที่ฝากไว้คงเหลือ: ${formatAmountRound(carryForward)} บาท`;
          }
        }
      }
      
      if (needsCompanyBank) {
        initialText += `\n--------------------------\n${companyBankText(gender)}`;
      }

      // Add existing payment link if available (only for company customers)
      if (usesPaymentLink && existingPaymentLink && existingPaymentLink.canReuse && existingPaymentLink.paymentUrl) {
        // Add credit card fee calculation if it's credit card payment
        if (existingPaymentLink.isCreditCard) {
          const originalAmount = parseFloat(String(result.grandTotal).replace(/[^\d.]/g, ''));
          const feeAmount = originalAmount * 0.035;
          const totalWithFee = originalAmount + feeAmount;
          initialText += `\n--------------------------\n ยอดรวม + ค่าธรรมเนียม 3.5% = ${(totalWithFee.toFixed(2))} บาท\n🔗 ลิงก์สำหรับชำระเงิน :\n${existingPaymentLink.paymentUrl}`;
        } else {
          initialText += `\n--------------------------\n🔗 ลิงก์สำหรับชำระเงิน :\n${existingPaymentLink.paymentUrl}`;
        }
      }
      
      setSummaryText(initialText);
    } else {
      // No customer selected (e.g. right after saving or "เก็บเงิน") — don't keep the last one's text.
      setSummaryText('');
    }
  }, [pendingOrders, gender, effectiveShop, existingPaymentLink, isCompanyCustomer, advanceAmount, usesPaymentLink, needsCompanyBank]);

  const handleCopy = () => {
    navigator.clipboard.writeText(summaryText)
      .then(() => {
        setCopyMessage('Copied!');
        if (creditCoversAll) setCopiedForSettlement(true);
      })
      .catch(() => setCopyMessage('Failed to copy'));

    setTimeout(() => setCopyMessage(''), 3000);
  };

  const handleCreateLink = async () => {
    if (!pendingOrders?.orders || pendingOrders.orders.length === 0) {
      toast.error('No orders available');
      return;
    }

    const urls = pendingOrders.orders.map(order => order.url).filter(Boolean);
    const smileName = pendingOrders.smileName;

    if (!smileName) {
      toast.error('Smile name not found');
      return;
    }

    try {
      const response = await createFirstOrderPaymentLink({
        urls,
        smileName,
        paymentMethod
      }, shopNameProp ?? undefined);
      
      if (response && response.success) {
        toast.success('Payment link created successfully!');
        console.log('Payment link response:', response);
        
        // Update Redux state with the new payment link
        if (response.data && response.data.paymentUrl) {
          dispatch(setExistingPaymentLink({
            canReuse: true,
            paymentUrl: response.data.paymentUrl,
            message: 'Payment link created successfully',
            isCreditCard: response.data.isCreditCard
          }));
        }
        
        // Add payment URL to the text area with credit card fee if applicable
        if (response.data.paymentUrl) {
          if (response.data.isCreditCard) {
            const originalAmount = parseFloat(String(grandTotalState).replace(/[^\d.]/g, ''));
            const feeAmount = originalAmount * 0.035;
            const totalWithFee = originalAmount + feeAmount;
            const paymentText = `\n--------------------------\n ยอดรวม + ค่าธรรมเนียม 3.5% = ${(totalWithFee.toFixed(2))} บาท\n🔗 ลิงก์สำหรับชำระเงิน :\n${response.data.paymentUrl}`;
            setSummaryText(prevText => prevText + paymentText);
          } else {
            const paymentText = `\n--------------------------\n🔗 ลิงก์สำหรับชำระเงิน :\n${response.data.paymentUrl}`;
            setSummaryText(prevText => prevText + paymentText);
          }
        }
      } else {
        toast.error(response?.message || 'Failed to create payment link');
      }
    } catch (error) {
      toast.error('Error creating payment link');
      console.error('Error creating payment link:', error);
    }
  };

  const handleTogglePaymentMethod = async () => {
    if (!pendingOrders?.orders || pendingOrders.orders.length === 0) {
      toast.error('No orders available');
      return;
    }

    const urls = pendingOrders.orders.map(order => order.url).filter(Boolean);
    const smileName = pendingOrders.smileName;
    const newPaymentMethod = isCreditCard ? 'qrCode' : 'creditCard';

    if (!smileName) {
      toast.error('Smile name not found');
      return;
    }

    try {
      const response = await createFirstOrderPaymentLink({
        urls,
        smileName,
        paymentMethod: newPaymentMethod
      }, shopNameProp ?? undefined);
      
      if (response && response.success) {
        toast.success(`Payment link updated to ${newPaymentMethod === 'creditCard' ? 'Credit Card' : 'QR Code'}!`);
        console.log('Payment link response:', response);
        
        // Update Redux state with the new payment link
        if (response.data && response.data.paymentUrl) {
          dispatch(setExistingPaymentLink({
            canReuse: true,
            paymentUrl: response.data.paymentUrl,
            message: 'Payment link updated successfully',
            isCreditCard: response.data.isCreditCard
          }));
        }
        
        // Update the text area with new payment URL and credit card fee if applicable
        if (response.data.paymentUrl) {
          let baseText = generateSummaryText(ordersSummary, grandTotalState, gender, effectiveShop === 'tama');
          
          if (response.data.isCreditCard) {
            const originalAmount = parseFloat(String(grandTotalState).replace(/[^\d.]/g, ''));
            const feeAmount = originalAmount * 0.035;
            const totalWithFee = originalAmount + feeAmount;
            const paymentText = `\n--------------------------\n ยอดรวม + ค่าธรรมเนียม 3.5% = ${(totalWithFee.toFixed(2))} บาท\n🔗 ลิงก์สำหรับชำระเงิน :\n${response.data.paymentUrl}`;
            setSummaryText(baseText + paymentText);
          } else {
            const paymentText = `\n--------------------------\n🔗 ลิงก์สำหรับชำระเงิน :\n${response.data.paymentUrl}`;
            setSummaryText(baseText + paymentText);
          }
        }
      } else {
        toast.error(response?.message || 'Failed to update payment link');
      }
    } catch (error) {
      toast.error('Error updating payment link');
      console.error('Error updating payment link:', error);
    }
  };

  const calculateTextAreaRows = (text) => {
    const lineCount = text.split('\n').length;
    const additionalPadding = 1;
    return lineCount + additionalPadding;
  };

  useEffect(() => {
    if (womenList.includes(user.nickname)) {
      setGender('คะ');
    }
  }, [user]);

  const textAreaRows = calculateTextAreaRows(summaryText);

  const hasExistingLink = existingPaymentLink && existingPaymentLink.canReuse;
  const isCreditCard = existingPaymentLink && existingPaymentLink.isCreditCard;

  return (
    <div>
      <div className='flex justify-end'>
        <span className='mr-auto text-xl font-bold'>ระบบเก็บเงิน</span>
        <div className='ml-auto mb-2 flex items-center gap-4'>
          {copyMessage && <span className='mx-3'>{copyMessage}</span>}
          
          {usesPaymentLink && !hasExistingLink && (
            <FormControl component="fieldset" className='mr-4'>
              <RadioGroup
                row
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
              >
                <FormControlLabel 
                  value="qrCode" 
                  control={<Radio size="small" />} 
                  label="QR Code" 
                />
                <FormControlLabel 
                  value="creditCard" 
                  control={<Radio size="small" />} 
                  label="Credit Card" 
                />
              </RadioGroup>
            </FormControl>
          )}
          
          {chargesWallet ? (
            <button
              className='bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium transition-colors duration-200'
              onClick={() => setIsChargeOpen(true)}
            >
              เก็บเงิน
            </button>
          ) : usesPaymentLink ? (
            hasExistingLink ? (
              <div className='flex gap-2'>
                <button 
                  className='bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-md font-medium transition-colors duration-200' 
                  onClick={handleTogglePaymentMethod}
                  disabled={isLoading}
                >
                  {isLoading ? 'Updating...' : `Switch to ${isCreditCard ? 'QR Code' : 'Credit Card'}`}
                </button>
                <button 
                  className='bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md font-medium transition-colors duration-200' 
                  onClick={handleCopy}
                >
                  Copy
                </button>
              </div>
            ) : (
              <button 
                className='bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed' 
                onClick={handleCreateLink}
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : 'สร้างลิ้งค์'}
              </button>
            )
          ) : (
            <div className='flex items-center gap-3'>
              <button
                className='btn-primary'
                onClick={handleCopy}
              >
                Copy
              </button>
              {creditCoversAll && copiedForSettlement && (
                <span className='text-amber-700 text-sm font-medium'>
                  ⚠️ แจ้งลูกค้าแล้ว — อย่าลืมไปตัดยอดปิดรายการที่ "จ่ายค่าส่ง"
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {needsCompanyBank && <div className='mb-2 p-2 rounded bg-red-100 text-red-700 font-bold'>{COMPANY_BANK_NOTICE}</div>}
      <textarea
        readOnly
        value={summaryText}
        rows={textAreaRows}
        className='w-full p-2 border border-gray-300 rounded-md'
      />
      {chargesWallet && (
        <AuctionChargeModal
          isOpen={isChargeOpen}
          onClose={() => setIsChargeOpen(false)}
          group={pendingOrders}
          onCharged={onWalletCharged}
        />
      )}
    </div>
  );
}

export default OrderAmount;
